import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Persisted state hook with cross-tab sync and debounced writes.
 *
 * Stores state in localStorage under `'roll1.' + key`. Supports:
 * - Optional `validate` type-guard for data integrity
 * - Debounced writes (default 300ms) to avoid excessive I/O
 * - Cross-tab sync via `StorageEvent`
 * - Functional updater `(prev: T) => T`
 * - Silent fallback on JSON.parse errors or QuotaExceeded
 *
 * @param key - Storage key (auto-prefixed with `roll1.`)
 * @param defaultValue - Fallback when nothing stored or data invalid
 * @param options - Optional debounce and validation settings
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  options?: {
    debounceMs?: number;
    validate?: (value: unknown) => value is T;
  },
): [T, (value: T | ((prev: T) => T)) => void] {
  const fullKey = `roll1.${key}`;
  const debounceMs = options?.debounceMs ?? 300;
  const validate = options?.validate;

  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(fullKey);
      if (stored === null) return defaultValue;
      const parsed: unknown = JSON.parse(stored);
      if (validate && !validate(parsed)) return defaultValue;
      return parsed as T;
    } catch {
      return defaultValue;
    }
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = typeof value === 'function'
          ? (value as (prev: T) => T)(prev)
          : value;

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          try {
            localStorage.setItem(fullKey, JSON.stringify(next));
          } catch {
            // QuotaExceeded — silent fallback
          }
        }, debounceMs);

        return next;
      });
    },
    [fullKey, debounceMs],
  );

  // Cross-tab sync
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== fullKey) return;
      if (e.newValue === null) {
        setState(defaultValue);
        return;
      }
      try {
        const parsed: unknown = JSON.parse(e.newValue);
        if (validate && !validate(parsed)) {
          setState(defaultValue);
          return;
        }
        setState(parsed as T);
      } catch {
        setState(defaultValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [fullKey, defaultValue, validate]);

  // Flush pending write and cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        // Flush to localStorage so no writes are lost when the component unmounts
        try {
          localStorage.setItem(fullKey, JSON.stringify(stateRef.current));
        } catch {
          // QuotaExceeded — silent fallback
        }
      }
    };
  }, [fullKey]);

  return [state, setValue];
}
